import React from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  CircularProgress,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import { 
  PlayArrow as RunningIcon,
  Schedule as ScheduledIcon, 
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  Timeline as ConcurrentIcon,
  Refresh as RefreshIcon,
  HourglassEmpty as BottleneckIcon,
  Speed as PerformanceIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const MetricsWidget = ({ 
  title, 
  value, 
  loading = false, 
  icon, 
  color = 'primary',
  description,
  onRefresh,
  lastUpdated,
  valueComponent
}) => {
  const getIcon = () => {
    switch (icon) {
      case 'running':
        return <RunningIcon sx={{ fontSize: 24, color: 'success.main' }} />;
      case 'scheduled':
        return <ScheduledIcon sx={{ fontSize: 24, color: 'warning.main' }} />;
      case 'completed':
        return <CompletedIcon sx={{ fontSize: 24, color: 'success.main' }} />;
      case 'failed':
        return <FailedIcon sx={{ fontSize: 24, color: 'error.main' }} />;
      case 'concurrent':
        return <ConcurrentIcon sx={{ fontSize: 24, color: 'info.main' }} />;
      case 'bottleneck':
        return <BottleneckIcon sx={{ fontSize: 24, color: 'error.main' }} />;
      case 'performance':
        return <PerformanceIcon sx={{ fontSize: 24, color: 'info.main' }} />;
      case 'schedule':
        return <ScheduleIcon sx={{ fontSize: 24, color: 'warning.main' }} />;
      default:
        return <RunningIcon sx={{ fontSize: 24, color: 'primary.main' }} />;
    }
  };

  const getStatusColor = () => {
    switch (icon) {
      case 'running':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'concurrent':
        return 'info';
      case 'bottleneck':
        return 'error';
      case 'performance':
        return 'info';
      case 'schedule':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
        border: `1px solid rgba(0,0,0,0.08)`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getIcon()}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
            {description && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        
        {onRefresh && (
          <Tooltip title="Refresh">
            <IconButton 
              size="small" 
              onClick={onRefresh}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Value */}
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <CircularProgress size={24} color={getStatusColor()} />
        ) : valueComponent ? (
          valueComponent
        ) : (
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: `${getStatusColor()}.main`,
              textAlign: 'center',
              lineHeight: 1
            }}
          >
            {value !== null && value !== undefined ? value.toLocaleString() : 'N/A'}
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Chip 
          label={icon?.toUpperCase() || 'METRIC'} 
          size="small" 
          color={getStatusColor()}
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
        
        {lastUpdated && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {new Date(lastUpdated).toLocaleTimeString()}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default MetricsWidget;
